'use client'

import { CookieNameEnum } from "@/src/constants/cookie-names"
import { DB_MENUDINO_NAME, MERCHANT_STORE_NAME } from "@/src/constants/defaults"
import { deleteCookie } from "cookies-next"
import { signOut, useSession } from "next-auth/react"
import { createContext, ReactNode, useContext, useEffect, useState } from "react"
import { useShoppingCart } from "../shopping-cart/shopping-cart-context"
import MerchantDb from "@/src/core/merchant-db"

type CustomerLoginStatus = 'notLoggedIn' | 'loggedIn' | 'verifying'

interface CustomerContextType {
    /** Dados do usuário logado. *Este valor ficará sendo atualizado conforme o `next-auth` for atualizado, portanto, cuidado colocá-lo como dependência do useEffect para evitar **excesso de chamadas**.* */
    customer: Customer.Response | Customer.Guest | Customer.Social | null
    setCustomer: (customer: Customer.Response | Customer.Guest | Customer.Social) => void
    customerLoginStatus: CustomerLoginStatus
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined)

const KEY_PATH = 'customer'

export const CustomerProvider = ({ children }: { children: ReactNode }) => {
    const [customer, setCustomer] = useState<Customer.Response | Customer.Guest | Customer.Social | null>(null)
    const [customerLoginStatus, setCustomerLoginStatus] = useState<CustomerLoginStatus>('notLoggedIn')
    const { removeRedeemedItemsAndCashback } = useShoppingCart()

    const { data: session } = useSession()

    const clearAndSignOut = async () => {
        setCustomerLoginStatus('notLoggedIn')
        removeRedeemedItemsAndCashback()
        await signOut({ redirect: false })
    }

    useEffect(() => {
        const saveCustomer = async () => {
            const db = await MerchantDb.getDB()

            if (db)
                await db.put(MERCHANT_STORE_NAME, { id: KEY_PATH, ...customer })
        }

        if (customer)
            saveCustomer()
    }, [customer])

    useEffect(() => {
        const getCustomer = async () => {
            if (session === undefined)
                setCustomerLoginStatus('verifying')
            else if (session === null)
                setCustomerLoginStatus('notLoggedIn')

            const db = await MerchantDb.getDB()

            if (session) {
                setCustomerLoginStatus('loggedIn')

                if (db) {
                    const storedCustomer = await db.get(MERCHANT_STORE_NAME, "customer")

                    if (storedCustomer) {
                        setCustomer(storedCustomer as Customer.Response)
                        return
                    }
                }

                await clearAndSignOut()
            }
            else if (session === null) {
                if (db)
                    await db.delete(MERCHANT_STORE_NAME, 1)

                deleteCookie(CookieNameEnum.UserAccessTokens)
                setCustomer(null)
                removeRedeemedItemsAndCashback()
            }
        }

        getCustomer()
    }, [session])

    useEffect(() => {
        const logOut = async () => {
            if (session?.error) {
                await clearAndSignOut()
            }
        }
        logOut()
    }, [session?.error])

    return (
        <CustomerContext.Provider value={{ customer, setCustomer, customerLoginStatus }}>
            {children}
        </CustomerContext.Provider>
    )
}

export const useCustomer = (): CustomerContextType => {
    const context = useContext(CustomerContext)

    if (!context)
        throw new Error('O contexto de Customer deve estar dentro do CustomerProvider.')

    return context
}